import { useCallback, useEffect, useState } from "react";
import { CloudSun, CloudRain, Cloud, Sun, Moon, Clock3, Users, Siren, Scale, HandCoins } from "lucide-react";
import api from "@/lib/api";

/* Skyline — the city right now: weather, time and the public pulse, from the same
   feed the server browser reads. Demo floor shows a canned evening. */

const DEMO = { snapshot: { weather: "Clear", game_time: "21:40", players: 12, max_players: 64 }, pulse: { citizens: 7, open_911: 2, court_cases: 2, pawn_listings: 3 } };

function weatherIcon(w) {
  const k = (w || "").toLowerCase();
  if (k.includes("rain") || k.includes("storm")) return CloudRain;
  if (k.includes("cloud") || k.includes("overcast")) return Cloud;
  const hour = new Date().getHours();
  if (k.includes("night") || hour < 6 || hour > 19) return Moon;
  return Sun;
}

export default function SkylineApp({ demo, serverId, accent }) {
  const [data, setData] = useState(demo ? DEMO : null);

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/citystats`)
      .then((r) => setData({ snapshot: r.data.server.snapshot || {}, pulse: r.data.pulse }))
      .catch(() => setData(DEMO));
  }, [demo, serverId]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const snap = data?.snapshot || {};
  const pulse = data?.pulse || {};
  const WIcon = weatherIcon(snap.weather) || CloudSun;

  return (
    <div data-testid="skyline-app" className="p-3 flex flex-col gap-3 min-h-full">
      <div className="flex items-center gap-2 px-1 pb-1">
        <CloudSun className="w-4 h-4" style={{ color: "#7dd3fc" }} />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">Skyline</p>
        <p className="ml-auto font-mono text-[7px] text-white/40 uppercase tracking-[0.1em]">bornwood, right now</p>
      </div>
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-sky-950/40 to-zinc-900/60 p-5 flex items-center gap-4">
        <WIcon className="w-14 h-14 text-sky-200" />
        <div>
          <p data-testid="skyline-weather" className="font-sans font-bold text-xl text-white">{snap.weather || "Clear"}</p>
          <p className="font-mono text-[10px] text-white/50 flex items-center gap-1.5 mt-1"><Clock3 className="w-3 h-3" /> city time {snap.game_time || "—"}</p>
          <p className="font-mono text-[8px] text-white/40 mt-0.5">{snap.players ?? 0}/{snap.max_players ?? 64} citizens on the streets</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[[Users, "Registered", pulse.citizens ?? 0], [Siren, "911 open", pulse.open_911 ?? 0],
          [Scale, "Court cases", pulse.court_cases ?? 0], [HandCoins, "Pawn shelf", pulse.pawn_listings ?? 0]].map(([Icon, label, v], i) => (
          <div key={label} data-testid={`skyline-stat-${i}`} className="rounded-2xl bg-white/5 border border-white/10 p-3">
            <p className="font-mono text-lg font-bold text-white">{v}</p>
            <p className="font-mono text-[7px] uppercase tracking-[0.15em] text-white/40 mt-0.5 flex items-center gap-1"><Icon className="w-3 h-3" />{label}</p>
          </div>
        ))}
      </div>
      <p className="mt-auto font-mono text-[7px] text-white/30 text-center uppercase tracking-[0.2em]">city pulse · refreshes every 30s</p>
    </div>
  );
}
